/**
 * The 56px top bar — breadcrumb on the left, the two placeholder affordances on the right.
 * Geometry from `design/project/screens/chrome.jsx`.
 *
 * The breadcrumb is `WORKSPACE › Projects` and stops there: project tabs are not built,
 * because no project screen exists to tab between (KLQ-20260819-003 out of scope).
 *
 * Search is live since `KLQ-20260905-001` phase 5: the box opens the ⌘K overlay, and the bar
 * owns the ⌘K / Ctrl+K listener because the bar is on every signed-in screen — "from anywhere in
 * the app" is "anywhere this bar is drawn". The bell is the inbox's (B3), the single slot the
 * inbox and notifications share — there is no second copy of either anywhere.
 */

import { ChevronRight, Menu, Search } from 'lucide-react';
import { Fragment, useState } from 'react';

import { useMessages } from '@/lib/i18n';

import { InboxBell } from '@/features/inbox';
import { SearchOverlay, useSearchShortcut } from '@/features/search';

import styles from './Chrome.module.css';

export interface TopBarProps {
  /**
   * Opens the navigation drawer. **Drawn only on a phone** — the stylesheet decides that, the
   * same way every other narrow-screen control in this product does. On a wide screen the rail
   * is always there and a button that opens what is already open is noise.
   */
  readonly onOpenNav: () => void;
  /**
   * Whether the bell asks for its count. `false` while the session is not ready to be asked —
   * the frame is drawn by screens that may render before the workspace is claimed, and a bell
   * that fetched there would be one request answering nothing.
   */
  readonly showInbox?: boolean;
  /**
   * Left to right, outermost first. An empty crumb is skipped rather than drawn as a gap —
   * the workspace name arrives one render after the screen does, and a lone `›` in the
   * meantime looks like a bug.
   */
  readonly crumbs: readonly string[];
}

export function TopBar({ crumbs, onOpenNav, showInbox = true }: TopBarProps) {
  const t = useMessages('app');
  const visible = crumbs.filter((crumb) => crumb !== '');
  // Mounting is opening (`SearchOverlay`): every open starts from an empty box.
  const [searching, setSearching] = useState(false);
  useSearchShortcut(() => {
    setSearching(true);
  });

  return (
    <div className={styles.bar}>
      {/* Where the workspace mark stands on a wide screen — the rail's top slot, which is
          exactly where a hand reaches for navigation when the rail itself is away. */}
      <button type="button" className={styles.navToggle} onClick={onOpenNav}>
        <Menu size={20} strokeWidth={1.5} aria-hidden />
        <span className="sr-only">{t('chrome.nav.open')}</span>
      </button>
      <nav className={styles.breadcrumb} aria-label={t('chrome.breadcrumb')}>
        {visible.map((crumb, index) => (
          <Fragment key={crumb}>
            {index > 0 ? (
              <ChevronRight
                className={styles.crumbSeparator}
                size={14}
                strokeWidth={1.5}
                aria-hidden
              />
            ) : null}
            <span
              className={
                index === visible.length - 1
                  ? [styles.crumb, styles.crumbCurrent].filter(Boolean).join(' ')
                  : styles.crumb
              }
              aria-current={index === visible.length - 1 ? 'page' : undefined}
            >
              {crumb}
            </span>
          </Fragment>
        ))}
      </nav>

      <div className={styles.barSpacer} />

      <div className={styles.barActions}>
        <button
          type="button"
          className={styles.search}
          aria-haspopup="dialog"
          aria-keyshortcuts="Meta+K Control+K"
          onClick={() => {
            setSearching(true);
          }}
        >
          <Search size={14} strokeWidth={1.5} aria-hidden />
          <span className={styles.searchLabel}>{t('chrome.nav.search')}</span>
          <span className={styles.searchShortcut} aria-hidden>
            {t('chrome.searchShortcut')}
          </span>
        </button>
        {/* The bell is live since B3 (`MCK-20260521-009`): it was the last placeholder in the
            chrome, and it is the slot the inbox was always going to land in. It reads the
            summary rather than a page — most screens never open the inbox. */}
        <InboxBell enabled={showInbox} />
      </div>
      {searching ? (
        <SearchOverlay
          onClose={() => {
            setSearching(false);
          }}
        />
      ) : null}
    </div>
  );
}
